import { reactive, ref } from 'vue'

import { calendarApi } from './api'
import { browserZone } from './lib/time.mjs'

// What every calendar screen shares: who the person is to the calendar (their
// zone, their calendars, what they may do), asked once per page load, and the
// editor — one for the whole application, so the mail reader, a record's file
// or the assistant can open it over whatever screen is showing.

const settings = ref(null)
let asking = null

export function useCalendarSettings() {
  async function load({ force = false } = {}) {
    if (settings.value && !force) return settings.value
    if (!asking || force) {
      asking = calendarApi.me()
        .then((data) => { settings.value = data; return data })
        .finally(() => { asking = null })
    }
    return asking
  }
  const zone = () => settings.value?.timezone || browserZone()
  return { settings, load, zone }
}

/**
 * The editor over any screen.
 *
 *   const { open } = useCalendarEditor()
 *   const saved = await open({ title, startDate, startTime, links, externals, source })
 *
 * `open` resolves with the saved event, or null when the person closes it —
 * nothing is saved without them pressing Save. `draft` has the API's own
 * fields; an `id` opens an existing event (with `occurrence` for a series).
 */
const editor = reactive({ open: false, draft: null, resolve: null, key: 0 })

export function useCalendarEditor() {
  function open(draft = {}) {
    if (editor.resolve) editor.resolve(null)
    return new Promise((resolve) => {
      editor.draft = { ...draft }
      editor.resolve = resolve
      editor.key += 1
      editor.open = true
    })
  }
  function close(result = null) {
    const resolve = editor.resolve
    editor.open = false
    editor.resolve = null
    resolve?.(result)
  }
  return { editor, open, close }
}

/** A way for any screen to ask the open agendas to reload. */
export const changes = ref(0)
export const touch = () => { changes.value += 1 }
